import { type ChangeEvent } from 'react'

import { useUnit } from 'effector-react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Slider } from '@/components/ui/slider.tsx'
import { TextField } from '@/components/ui/text-field'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import {
  $aiSettings,
  updateIntroduceTyposFx,
} from '@/modules/ai-features/models/ai-settings.ts'
import { SettingsPageTitle } from '@/modules/creator-settings/SettingsPageTitle.tsx'
import { useDebouncedProperty } from '@/utils/useDebouncedProperty.ts'

import { setTypoFrequencyFx } from '../../models/typo-settings'

export const TypoSettings = () => {
  const aiSettings = useUnit($aiSettings)

  const [frequency, setFrequency] = useDebouncedProperty<number | undefined>(
    aiSettings?.typoFrequency,
    (value) => setTypoFrequencyFx(value ?? 2),
  )

  const handleChangeFrequency = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const parsedValue = parseFloat(value)

      if (value === '' || (parsedValue >= 0 && parsedValue <= 100)) {
        setFrequency(value === '' ? undefined : parsedValue)
      }
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <SettingsPageTitle
        title={<span className="text-xl font-medium">Typo Generation</span>}
        uniqKey="typo-settings-toggle"
        description={
          <span className="flex items-center gap-2">
            Set a percantage of purposfully added typing mistakes by our Ai
            Translator to your sent out messages.
          </span>
        }
        toggleName="Activate"
        enabled={aiSettings?.introduceTypos || false}
        onChange={() => updateIntroduceTyposFx(!aiSettings?.introduceTypos)}
      />
      {aiSettings?.introduceTypos && (
        <div className="flex flex-col w-1/2 gap-6">
          <label
            htmlFor="word-input"
            className="text-sm font-medium text-text-default flex items-center gap-1"
          >
            Typo percentage
            <Tooltip content="This setting shows how many typos will be added per 1000 words.">
              <InfoIcon className="text-secondary-300" />
            </Tooltip>
          </label>
          <div className="flex items-center gap-6">
            <Slider
              min={0}
              max={100}
              step={0.1}
              className="w-52"
              value={[frequency ?? 0]}
              onValueChange={(values) => setFrequency(values[0])}
            />

            <TextField
              type="number"
              min={0}
              max={100}
              value={frequency}
              onChange={handleChangeFrequency}
              suffix="%"
              className="h-10 w-20 min-w-20 rounded-md px-2 py-1 text-m  text-secondary-300 "
            />
          </div>

          <p className="text-xs text-secondary-300">
            With {aiSettings?.typoFrequency}% typos out of 1000 words, around{' '}
            {Math.round(aiSettings?.typoFrequency * 10)} words will be
            misspelled.
          </p>
        </div>
      )}
    </div>
  )
}
